import { Epic } from '@superthread-com/api';
import { ModalId } from '@/types/modal';
import { getCurrentInstance } from 'vue';
import getDashedTitle from '@/utilities/getDashedTitle';
import useTranslate from '@/utilities/composables/useTranslate';
import constants from '@/utilities/constants';
import { nanoid } from 'nanoid';
import EventBus from '@/utilities/eventBus';

export default function useConvertCardToEpic() {
  const _this = getCurrentInstance()?.proxy as any;
  if (!_this) return;

  const { $store } = _this;
  const { dispatch, getters } = $store;
  const { translate } = useTranslate();

  // display notification indicating failure of conversion and option to retry
  const displayRetryNotification = (cardId: string): void => {
    const notifId = nanoid(4);
    dispatch('addUiNotification', {
      id: notifId,
      message: translate('couldNotConvertCardToEpic'),
      status: constants.uiNotificationStatuses.error,
      labels: [
        {
          text: translate('retry'),
          type: constants.uiNotificationElements.button,
          buttonStyle: 'important',
          callback: () => {
            dispatch('removeUiNotificationById', notifId);
            convertCardToEpic(cardId, true);
          },
        },
      ],
    });
  };

  // display notification indicating success of conversion
  const displaySuccessNotification = (epic: Epic): void => {
    dispatch('addUiNotification', {
      message: translate('cardSuccessfullyConvertedToEpic'),
      labels: [
        {
          text: translate('open'),
          type: constants.uiNotificationElements.button,
          buttonStyle: 'important',
          callback: () => {
            const dashedEpicTitle = getDashedTitle(epic.title);
            const path = `/${getters.getTeamSlug}/${constants.epicRoute.path}-${epic.id}-${dashedEpicTitle}`;
            const query = { userNavigated: true };
            dispatch('setDisplayedCards', [epic.id]);
            _this.$router.push({ path, query });
          },
        },
      ],
    });
  };

  // displays notification indicating that epics board has no lists
  const displayNoListsInEpicsBoardNotification = (): void => {
    dispatch('addUiNotification', {
      message: translate('roadmapMustHaveAtLeastOneStatus'),
      status: constants.uiNotificationStatuses.error,
    });
  };

  const closeCard = (cardId: string): void => {
    EventBus.$emit('closeCard', { cardId });
  };

  const openCard = (cardId: string): void => {
    dispatch('setDisplayedCards', [cardId]);
    const card = getters.getCardById(cardId);
    _this.$router.push({
      path: `/${getters.getTeamSlug}/card-${cardId}-${getDashedTitle(card.title)}`,
      query: { userNavigated: true },
    });
  };

  // function to be called when user confirms conversion
  const confirmConversion = async (cardId: string) => {
    if (getters.getIsModalDisplayed) {
      dispatch('setModal');
    }

    const cardWasDisplayed = getters.getIsCardDisplayed(cardId);
    try {
      if (cardWasDisplayed) closeCard(cardId);
      const epic = await dispatch('convertCardToEpic', cardId);
      displaySuccessNotification(epic);
    } catch (e: any) {
      if (cardWasDisplayed) openCard(cardId);
      if (e?.message === constants.noListsInEpicsBoardErr) displayNoListsInEpicsBoardNotification();
      else displayRetryNotification(cardId);
    }
  };

  // display confirmation modal before converting card to epic
  // with description stating which fields that will be lost after conversion
  const displayConfirmModal = (cardId: string): void => {
    const card = getters.getCardById(cardId);
    const descArr = [];

    const hasLinkedCards = card.linked_cards?.length > 0;
    if (hasLinkedCards) {
      descArr.push(translate('relatedCards').toLowerCase());
    }

    if (card.tags?.filter((tag: any) => tag.project_id)?.length > 0) {
      descArr.push(translate('spaceLevelTags').toLowerCase());
    }

    if (card.estimate > 0) {
      descArr.push(translate('effortEstimate').toLowerCase());
    }

    if (Object.keys(card.parent_card || {}).length > 0) {
      descArr.push(translate('parentCard').toLowerCase());
    } else if (Object.keys(card.epic || {}).length > 0) {
      descArr.push(translate('existingEpic').toLowerCase());
    }

    const description =
      descArr.length > 0 ? translate('convertedCardWillLose', { arr: descArr }) : '';

    dispatch('setModal', {
      id: ModalId.CardActionConfirm,
      meta: {
        title: translate('convertCardToEpic'),
        description,
        confirmButtonText: translate('convert'),
        onConfirmFn: () => confirmConversion(cardId),
      },
    });
  };

  const convertCardToEpic = (cardId: string, retry: boolean = false): void => {
    if (retry) {
      confirmConversion(cardId);
      return;
    }

    displayConfirmModal(cardId);
  };

  return {
    // helper functions (added to return for ease for testing)
    displayRetryNotification,
    displaySuccessNotification,
    displayNoListsInEpicsBoardNotification,
    closeCard,
    openCard,
    confirmConversion,
    displayConfirmModal,

    // main funtion
    convertCardToEpic,
  };
}
